<script setup lang="ts">
import { computed } from 'vue';
import { Line } from 'vue-chartjs';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import BaseCard from '@/components/ui/BaseCard.vue';
import type { WeeklyTrendPoint } from '@/api/dashboard-types';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

const props = defineProps<{
  trend: WeeklyTrendPoint[];
  loading?: boolean;
}>();

const chartData = computed(() => ({
  labels: props.trend.map((p) => {
    // Format "2026-05-12" → "May 12"
    const [, month, day] = p.date.split('-').map(Number);
    const d = new Date(2000, (month ?? 1) - 1, day ?? 1);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }),
  datasets: [
    {
      label: 'Issues',
      data: props.trend.map((p) => p.count),
      // Navy series stroke — per visual contract §3
      borderColor: 'oklch(42% 0.11 250)',
      backgroundColor: 'oklch(42% 0.11 250 / 0.08)',
      borderWidth: 2,
      tension: 0.3,
      fill: true,
      pointRadius: 4,
      pointHoverRadius: 6,
    },
  ],
}));

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: { mode: 'index' as const, intersect: false },
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: { stepSize: 1 },
      grid: { color: 'oklch(91% 0.008 250)' },
    },
    x: {
      grid: { display: false },
    },
  },
}));
</script>

<template>
  <BaseCard padding="md" data-testid="weekly-trend-chart">
    <template #header>
      <h2 class="text-section text-fg">Issues this week</h2>
    </template>

    <div v-if="loading" class="h-40 bg-canvas-soft rounded animate-pulse" />

    <div v-else style="min-height: 160px; position: relative;">
      <Line :data="chartData" :options="chartOptions" />
    </div>
  </BaseCard>
</template>
